import { CreateFarmPrimaryDataQueryParams, axios } from "utils";

import { PUBLIC_FOOTPRINTING_URL as endpoint } from "consts";

import {
  BeefFeedProduction,
  BeefFootprintReturnBody,
  CalculatedBeefFootprintResponse,
  CalculatedCropFootprintResponse,
  CalculatedDairyFootprintResponse,
  CalculatedFarmFootprintResponse,
  CalculatedPigletFootprintResponse,
  CalculatedPorkFootprintResponse,
  CropAssessment,
  CropFootprintReturnBody,
  CropSaleData,
  DairyFeedProduction,
  DairyFootprintReturnBody,
  EmissionsFactorRefrigerant,
  FarmFootprintReturnBody,
  FarmLandUseChange,
  FootprintingBeefFeedInformation,
  FootprintingBeefGrasslandFertiliser,
  FootprintingBeefGrasslandFertiliserPayload,
  FootprintingBeefHerdDetails,
  FootprintingBeefHerdSize,
  FootprintingBeefManureAdditivePayload,
  FootprintingBeefPrimaryData,
  FootprintingCropDrying,
  FootprintingCropFertilizer,
  FootprintingCropPrimaryData,
  FootprintingCropSalesPayload,
  FootprintingCropSeeding,
  FootprintingCropTillage,
  FootprintingDairyCowSales,
  FootprintingDairyFeedAdditivePayload,
  FootprintingDairyFeedInformation,
  FootprintingDairyGrasslandFertiliserPayload,
  FootprintingDairyHerdDetails,
  FootprintingDairyHerdSize,
  FootprintingDairyManureAdditivePayload,
  FootprintingDairyManureStoragePayload,
  FootprintingDairyPrimaryData,
  FootprintingDairyRefrigerantUsage,
  FootprintingFarmCropPlantProtectionPayload,
  FootprintingFarmPrimaryData,
  FootprintingFarmResourceConsumption,
  FootprintingPigletFeedInformation,
  FootprintingPigletHerdDetails,
  FootprintingPigletManureAdditivePayload,
  FootprintingPigletManureStoragePayload,
  FootprintingPigletPrimaryData,
  FootprintingPigletStablesPayload,
  FootprintingPorkFeedInformation,
  FootprintingPorkHerdDetails,
  FootprintingPorkManureAdditivePayload,
  FootprintingPorkManureStoragePayload,
  FootprintingPorkPigletSourcingPayload,
  FootprintingPorkPrimaryData,
  FootprintingPorkStablesPayload,
  LandUseChangeRequestPayload,
  PigletFeedProduction,
  PigletFootprintReturnBody,
  PorkFeedProduction,
  PorkFootprintReturnBody,
  PurchaserData,
  UploadableBeefFeedAdditive,
  UploadableBeefFeedInformation,
  UploadableBeefHerdSize,
  UploadableBeefManureStorage,
  UploadableCowSales,
  UploadableFeedAdditive,
  UploadableFeedInformation,
  UploadableGrasslandFertiliser,
  UploadableHerdSize,
  UploadableManureAdditive,
  UploadableManureStorage,
  UploadablePigletFeedInformation,
  UploadablePlantProtection,
  UploadablePorkFeedInformation,
  UploadableRefrigerant,
} from "models";

// Emission Factors

export const getFootprintingEmissionFactorsRefrigerants = async (): Promise<EmissionsFactorRefrigerant[]> =>
  axios.get(`${endpoint}/emission-factors/refrigerants`);

// Farm Data

export const getCalculatedFarmFootprintByYear = async (farmId: string, year: number): Promise<CalculatedFarmFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/footprint-calculation`, { data: { farmId, year }, hideErrorMessage: true });

export const getFarmFootprintByYear = async (farmId: string, year: number): Promise<FarmFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints`, { data: { farmId, year }, hideErrorMessage: true });

export const getFootprintingFarmPrimaryDataById = async (primaryDataId: string): Promise<FootprintingFarmPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingFarmPrimaryDataByYear = async (farmId: string, year: number): Promise<FootprintingFarmPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/primary-data`, { data: { farmId, year }, hideErrorMessage: true });

export const getFootprintingFarmResourceConsumptionByPrimaryDataId = async (
  footprintingFarmPrimaryDataId: string
): Promise<FootprintingFarmResourceConsumption> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/resource-consumption`, { data: { footprintingFarmPrimaryDataId }, hideErrorMessage: true });

export const setFootprintingFarmResourceConsumption = async (
  data: FootprintingFarmResourceConsumption,
  primaryDataId?: string
): Promise<FootprintingFarmResourceConsumption> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/farm-footprints/${primaryDataId}/resource-consumption`, data);
  }

  return axios.post(`${endpoint}/farm-footprints/resource-consumption`, data);
};

export const setFootprintingFarmPrimaryData = async (
  data: Omit<FootprintingFarmPrimaryData, "id">,
  primaryDataId?: string,
  options?: { autofillYear?: number }
): Promise<FootprintingFarmPrimaryData> => {
  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;

  if (primaryDataId && primaryDataId !== "") {
    return axios.put(`${endpoint}/farm-footprints/${primaryDataId}/primary-data`, data);
  }

  return axios.post(`${endpoint}/farm-footprints/primary-data${autofillQuery}`, data);
};

export const getFarmLandUseChanges = async (farmId: string, year?: number, range?: number): Promise<FarmLandUseChange[]> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`, { hideErrorMessage: true });

export const setFarmLandUseChanges = async (farmId: string, data: LandUseChangeRequestPayload, year: number, range: number): Promise<FarmLandUseChange[]> =>
  axios.put(`${endpoint}/farm-footprints/${farmId}/land-use-change?year=${year ?? ""}&range=${range ?? ""}`, data);

// Crop Data

export const getCalculatedCropFootprintByCropIdAndYear = async (farmId: string, year: number, cropId: string): Promise<CalculatedCropFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/footprint-calculation`, { data: { farmId, cropId, year }, hideErrorMessage: true });

export const getCropFootprintByCropIdAndYear = async (farmId: string, year: number, cropId: string): Promise<CropFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints`, { data: { farmId, cropId, year }, hideErrorMessage: true });

export const getFootprintingCropPrimaryDataById = async (primaryDataId: string): Promise<FootprintingCropPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingCropPrimaryDataByCropIdAndYear = async (farmId: string, year: number, cropId: string): Promise<FootprintingCropPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/crop-footprints/primary-data`, { data: { farmId, cropId, year }, hideErrorMessage: true });

export const setFootprintingCropPrimaryData = async (
  data: Omit<FootprintingCropPrimaryData, "id">,
  primaryDataId?: string,
  options?: { autofillYear?: number }
): Promise<FootprintingCropPrimaryData> => {
  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;

  if (primaryDataId) {
    return axios.put(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`, data);
  }

  return axios.post(`${endpoint}/crop-footprints/primary-data${autofillQuery}`, data);
};

export const deleteFootprintingCropPrimaryData = async (primaryDataId: string) => axios.delete(`${endpoint}/crop-footprints/${primaryDataId}/primary-data`);

export const getFootprintingCropPlantProtection = async (primaryDataId: string): Promise<UploadablePlantProtection[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/plant-protection`);

export const setFootprintingCropPlantProtection = async (
  data: FootprintingFarmCropPlantProtectionPayload,
  primaryDataId?: string
): Promise<UploadablePlantProtection[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/plant-protection`, data.selectedPlantProtection);

export const getFootprintingCropDryingsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropDrying[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/dryings`);

export const setFootprintingCropDryingsByPrimaryDataId = async (data: FootprintingCropDrying[], primaryDataId: string) =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/dryings`, data);

export const getFootprintingCropSeedingsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropSeeding[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/seedings`);

export const setFootprintingCropSeedingsByPrimaryDataId = async (data: FootprintingCropSeeding[], primaryDataId: string) =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/seedings`, data);

export const getFootprintingCropTillagesByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropTillage[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/tillages`);

export const setFootprintingCropTillagesByPrimaryDataId = async (data: FootprintingCropTillage[], primaryDataId: string) =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/tillages`, data);

export const getFootprintingCropFertilizersMineralsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-minerals`, { data: { primaryDataId } });

export const setFootprintingCropFertilizersMinerals = async (
  data: FootprintingCropFertilizer[],
  primaryDataId: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-minerals`, data);

export const getFootprintingCropFertilizersOrganicsByPrimaryDataId = async (primaryDataId: string): Promise<FootprintingCropFertilizer[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-organics`, { data: { primaryDataId } });

export const setFootprintingCropFertilizersOrganics = async (
  data: FootprintingCropFertilizer[],
  primaryDataId?: string
): Promise<FootprintingCropFertilizer[]> => axios.put(`${endpoint}/crop-footprints/${primaryDataId}/fertilizers-organics`, data);

export const getFootprintingCropPurchasersByFarmId = async (farmId: string): Promise<PurchaserData[]> =>
  axios.get(`${endpoint}/crop-footprints/purchasers?farmId=${farmId}`);

export const getFootprintingCropSalesByPrimaryDataId = async (primaryDataId: string): Promise<CropSaleData[]> =>
  axios.get(`${endpoint}/crop-footprints/${primaryDataId}/sales`);

export const setFootprintingCropSales = async (data: FootprintingCropSalesPayload, primaryDataId: string): Promise<CropSaleData[]> =>
  axios.put(`${endpoint}/crop-footprints/${primaryDataId}/sales`, data);

export const getFootprintingCropAssessmentsRequiringReview = async (farmId: string): Promise<{ [year: string]: { [cropId: string]: CropAssessment } }> =>
  axios.get(`${endpoint}/crop-assessments-requiring-review?farmId=${farmId}`);

// Dairy Data

export const getDairyFootprintByYear = async (farmId: string, year: number): Promise<DairyFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints`, { data: { farmId, year }, hideErrorMessage: true });

export const getCalculatedDairyFootprintByYear = async (farmId: string, year: number): Promise<CalculatedDairyFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints/footprint-calculation`, { data: { farmId, year }, hideErrorMessage: true });

export const getFootprintingDairyPrimaryDataById = async (primaryDataId: string): Promise<FootprintingDairyPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingDairyPrimaryDataByYear = async (farmId: string, year: number): Promise<FootprintingDairyPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/dairy-footprints/primary-data`, { data: { farmId, year }, hideErrorMessage: true });

export const setFootprintingDairyPrimaryData = async (
  data: Omit<FootprintingDairyPrimaryData, "id">,
  primaryDataId?: string,
  options?: CreateFarmPrimaryDataQueryParams
): Promise<FootprintingDairyPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/primary-data`, data);
  }

  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;
  return axios.post(`${endpoint}/dairy-footprints/primary-data${autofillQuery}`, data);
};

export const deleteFootprintingDairyByPrimaryDataId = async (primaryDataId: string) =>
  axios.delete(`${endpoint}/dairy-footprints/${primaryDataId}/primary-data`);

// Beef Data

export const getBeefFootprintByYear = async (farmId: string, year: number): Promise<BeefFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/beef-footprints`, { data: { farmId, year }, hideErrorMessage: true });

export const getCalculatedBeefFootprintByYear = async (farmId: string, year: number): Promise<CalculatedBeefFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/beef-footprints/footprint-calculation`, { data: { farmId, year }, hideErrorMessage: true });

export const getFootprintingBeefPrimaryDataById = async (primaryDataId: string): Promise<FootprintingBeefPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const setFootprintingBeefPrimaryData = async (
  data: Omit<FootprintingBeefPrimaryData, "id">,
  primaryDataId?: string,
  options?: CreateFarmPrimaryDataQueryParams
): Promise<FootprintingBeefPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/beef-footprints/${primaryDataId}/primary-data`, data);
  }

  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;
  return axios.post(`${endpoint}/beef-footprints/primary-data${autofillQuery}`, data);
};

export const deleteFootprintingBeefByPrimaryDataId = async (primaryDataId: string) => axios.delete(`${endpoint}/beef-footprints/${primaryDataId}/primary-data`);

export const getBeefHerdSize = async (primaryDataId: string): Promise<FootprintingBeefHerdSize[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/herd-sizes`);

export const setBeefHerdSize = async (primaryDataId: string, data: UploadableBeefHerdSize[]): Promise<FootprintingBeefHerdSize[]> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/herd-sizes`, data);

export const getBeefHerdDetails = async (primaryDataId: string): Promise<FootprintingBeefHerdDetails> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/herd-details`);

export const setBeefHerdDetails = async (primaryDataId: string, data: Omit<FootprintingBeefHerdDetails, "id">): Promise<FootprintingBeefHerdDetails> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/herd-details`, data);

export const getBeefFeedAdditive = async (primaryDataId: string): Promise<UploadableBeefFeedAdditive[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/feed-additives`);

export const setBeefFeedAdditive = async (primaryDataId: string, data: UploadableBeefFeedAdditive[]): Promise<UploadableBeefFeedAdditive[]> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/feed-additives`, data);

export const getBeefFeedInformation = async (primaryDataId: string): Promise<FootprintingBeefFeedInformation[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/feed-informations`);

export const setBeefFeedInformation = async (primaryDataId: string, data: UploadableBeefFeedInformation[]): Promise<FootprintingBeefFeedInformation[]> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/feed-informations`, data);

export const getBeefGrasslandFertiliser = async (primaryDataId: string): Promise<FootprintingBeefGrasslandFertiliser[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/grassland-fertilisers`);

export const setBeefGrasslandFertiliser = async (
  payload: FootprintingBeefGrasslandFertiliserPayload,
  primaryDataId: string
): Promise<FootprintingBeefGrasslandFertiliser[]> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/grassland-fertilisers`, payload.selectedGrasslandFertilisers);

export const getBeefManureStorage = async (primaryDataId: string): Promise<UploadableBeefManureStorage[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/manure-storages`);

export const setBeefManureStorage = async (primaryDataId: string, data: UploadableBeefManureStorage[]): Promise<UploadableBeefManureStorage[]> =>
  axios.put(`${endpoint}/beef-footprints/${primaryDataId}/manure-storages`, data);

export const getFootprintingBeefManureAdditive = async (primaryDataId: string): Promise<UploadableManureAdditive[]> =>
  axios.get(`${endpoint}/beef-footprints/${primaryDataId}/manure-additives`);

export const setFootprintingBeefManureAdditive = async (
  data: FootprintingBeefManureAdditivePayload,
  primaryDataId?: string
): Promise<UploadableManureAdditive[]> => axios.put(`${endpoint}/beef-footprints/${primaryDataId}/manure-additives`, data.selectedManureAdditive);

export const getFootprintingBeefFeedProduction = async (feedInformationId: string): Promise<BeefFeedProduction | null> =>
  axios.get(`${endpoint}/beef-footprints/beef-feed-productions/${feedInformationId}`);

export const setFootprintingBeefFeedProduction = async (feedInformationId: string, data: BeefFeedProduction): Promise<BeefFeedProduction> =>
  axios.put(`${endpoint}/beef-footprints/beef-feed-productions/${feedInformationId}`, data);

export const getDairyHerdSize = async (primaryDataId: string): Promise<FootprintingDairyHerdSize[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/herd-sizes`);

export const setDairyHerdSize = async (primaryDataId: string, data: UploadableHerdSize[]): Promise<FootprintingDairyHerdSize[]> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/herd-sizes`, data);

export const getDairyHerdDetails = async (primaryDataId: string): Promise<FootprintingDairyHerdDetails> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/herd-details`);

export const setDairyHerdDetails = async (data: Omit<FootprintingDairyHerdDetails, "id">, primaryDataId?: string): Promise<FootprintingDairyHerdDetails> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/herd-details`, data);
  }

  return axios.post(`${endpoint}/dairy-footprints/herd-details`, data);
};

export const getDairyCowSales = async (primaryDataId: string): Promise<FootprintingDairyCowSales> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/cow-sales`);

export const setDairyCowSales = async (primaryDataId: string, data: UploadableCowSales): Promise<FootprintingDairyCowSales> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/cow-sales`, data);

export const getFootprintingDairyRefrigerantUsage = async (primaryDataId: string): Promise<FootprintingDairyRefrigerantUsage[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/refrigerant-usage`);

export const setFootprintingDairyRefrigerantUsage = async (
  data: UploadableRefrigerant[],
  primaryDataId?: string
): Promise<FootprintingDairyRefrigerantUsage[]> => axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/refrigerant-usage`, data);

export const getFootprintingDairyFeedAdditive = async (primaryDataId: string): Promise<UploadableFeedAdditive[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/feed-additives`);

export const setFootprintingDairyFeedAdditive = async (data: FootprintingDairyFeedAdditivePayload, primaryDataId?: string): Promise<UploadableFeedAdditive[]> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/feed-additives`, data.selectedFeedAdditive);

export const getFootprintingDairyGrasslandFertiliser = async (primaryDataId: string): Promise<UploadableGrasslandFertiliser[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/grassland-fertilisers`);

export const setFootprintingDairyGrasslandFertiliser = async (
  data: FootprintingDairyGrasslandFertiliserPayload,
  primaryDataId: string
): Promise<UploadableGrasslandFertiliser[]> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/grassland-fertilisers`, data.selectedGrasslandFertilisers);

export const getDairyFeedInformation = async (primaryDataId: string): Promise<FootprintingDairyFeedInformation[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/feed-informations`);

export const setDairyFeedInformation = async (primaryDataId: string, data: UploadableFeedInformation[]): Promise<FootprintingDairyFeedInformation> =>
  axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/feed-informations`, data);

export const getFootprintingDairyFeedProduction = async (feedInformationId: string): Promise<DairyFeedProduction | null> =>
  axios.get(`${endpoint}/dairy-footprints/dairy-feed-productions/${feedInformationId}`);

export const setFootprintingDairyFeedProduction = async (feedInformationId: string, data: DairyFeedProduction): Promise<DairyFeedProduction> =>
  axios.put(`${endpoint}/dairy-footprints/dairy-feed-productions/${feedInformationId}`, data);

export const getFootprintingDairyManureStorage = async (primaryDataId: string): Promise<UploadableManureStorage[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/manure-storages`);

export const setFootprintingDairyManureStorage = async (
  data: FootprintingDairyManureStoragePayload,
  primaryDataId?: string
): Promise<UploadableManureStorage[]> => axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/manure-storages`, data.selectedManureStorage);

export const getFootprintingDairyManureAdditive = async (primaryDataId: string): Promise<UploadableManureAdditive[]> =>
  axios.get(`${endpoint}/dairy-footprints/${primaryDataId}/manure-additives`);

export const setFootprintingDairyManureAdditive = async (
  data: FootprintingDairyManureAdditivePayload,
  primaryDataId?: string
): Promise<UploadableManureAdditive[]> => axios.put(`${endpoint}/dairy-footprints/${primaryDataId}/manure-additives`, data.selectedManureAdditive);

// Pork endpoints

export const getPorkFootprintByYear = async (year: number): Promise<PorkFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/pork-footprints`, { data: { year }, hideErrorMessage: true });

export const getCalculatedPorkFootprintByYear = async (year: number): Promise<CalculatedPorkFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/pork-footprints/footprint-calculation`, { data: { year }, hideErrorMessage: true });

export const getFootprintingPorkPrimaryDataById = async (primaryDataId: string): Promise<FootprintingPorkPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/pork-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingPorkPrimaryDataByYear = async (year: number): Promise<FootprintingPorkPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/pork-footprints/primary-data`, { data: { year }, hideErrorMessage: true });

export const setFootprintingPorkPrimaryData = async (
  data: Partial<Omit<FootprintingPorkPrimaryData, "id">>,
  primaryDataId?: string,
  options?: { autofillYear?: number }
): Promise<FootprintingPorkPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/pork-footprints/${primaryDataId}/primary-data`, data);
  }

  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;
  return axios.post(`${endpoint}/pork-footprints/primary-data${autofillQuery}`, data);
};

export const deleteFootprintingProkByPrimaryDataId = async (primaryDataId: string) => axios.delete(`${endpoint}/pork-footprints/${primaryDataId}/primary-data`);

export const getPorkHerdDetails = async (primaryDataId: string): Promise<FootprintingPorkHerdDetails> =>
  axios.get(`${endpoint}/pork-footprints/${primaryDataId}/herd-details`);

export const setPorkHerdDetails = async (
  data: Partial<Omit<FootprintingPorkHerdDetails, "id" | "primaryDataId">>,
  primaryDataId?: string
): Promise<FootprintingPorkHerdDetails> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/pork-footprints/${primaryDataId}/herd-details`, data);
  }

  return axios.post(`${endpoint}/pork-footprints/herd-details`, data);
};

export const getPorkFeedInformation = async (primaryDataId: string): Promise<UploadablePorkFeedInformation[]> =>
  axios.get(`${endpoint}/pork-footprints/${primaryDataId}/feed-informations`);

export const setPorkFeedInformation = async (primaryDataId: string, data: UploadablePorkFeedInformation[]): Promise<FootprintingPorkFeedInformation> =>
  axios.put(`${endpoint}/pork-footprints/${primaryDataId}/feed-informations`, data);

export const getFootprintingPorkFeedProduction = async (feedInformationId: string): Promise<PorkFeedProduction | null> =>
  axios.get(`${endpoint}/pork-footprints/pork-feed-productions/${feedInformationId}`);

export const setFootprintingPorkFeedProduction = async (feedInformationId: string, data: PorkFeedProduction): Promise<PorkFeedProduction> =>
  axios.put(`${endpoint}/pork-footprints/pork-feed-productions/${feedInformationId}`, data);

export const getFootprintingPorkManureStorage = async (primaryDataId: string): Promise<UploadableManureStorage[]> =>
  axios.get(`${endpoint}/pork-footprints/${primaryDataId}/manure-storages`);

export const setFootprintingPorkManureStorage = async (
  data: FootprintingPorkManureStoragePayload,
  primaryDataId?: string
): Promise<UploadableManureStorage[]> => axios.put(`${endpoint}/pork-footprints/${primaryDataId}/manure-storages`, data.selectedManureStorage);

export const getFootprintingPorkManureAdditive = async (primaryDataId: string): Promise<UploadableManureAdditive[]> =>
  axios.get(`${endpoint}/pork-footprints/${primaryDataId}/manure-additives`);

export const setFootprintingPorkManureAdditive = async (
  data: FootprintingPorkManureAdditivePayload,
  primaryDataId?: string
): Promise<UploadableManureAdditive[]> => axios.put(`${endpoint}/pork-footprints/${primaryDataId}/manure-additives`, data.selectedManureAdditive);

export const setPorkPigletSourcing = async (
  data: FootprintingPorkPigletSourcingPayload,
  primaryDataId: string
): Promise<FootprintingPorkPigletSourcingPayload> => axios.put(`${endpoint}/pork-footprints/${primaryDataId}/piglet-sourcing`, data);

export const setPorkStables = async (data: FootprintingPorkStablesPayload, primaryDataId: string): Promise<FootprintingPorkStablesPayload> =>
  axios.put(`${endpoint}/pork-footprints/${primaryDataId}/stables`, data);

// Piglet endpoints

export const getPigletFootprintByYear = async (year: number): Promise<PigletFootprintReturnBody> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/piglet-footprints`, { data: { year }, hideErrorMessage: true });

export const getCalculatedPigletFootprintByYear = async (year: number): Promise<CalculatedPigletFootprintResponse> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/piglet-footprints/footprint-calculation`, { data: { year }, hideErrorMessage: true });

export const getFootprintingPigletPrimaryDataById = async (primaryDataId: string): Promise<FootprintingPigletPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/piglet-footprints/${primaryDataId}/primary-data`, { hideErrorMessage: true });

export const getFootprintingPigletPrimaryDataByYear = async (year: number): Promise<FootprintingPigletPrimaryData> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${endpoint}/piglet-footprints/primary-data`, { data: { year }, hideErrorMessage: true });

export const setFootprintingPigletPrimaryData = async (
  data: Partial<Omit<FootprintingPigletPrimaryData, "id">>,
  primaryDataId?: string,
  options?: { autofillYear?: number }
): Promise<FootprintingPigletPrimaryData> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/primary-data`, data);
  }

  const autofillQuery = options?.autofillYear === undefined ? "" : `?autofillYear=${options.autofillYear}`;
  return axios.post(`${endpoint}/piglet-footprints/primary-data${autofillQuery}`, data);
};

export const deleteFootprintingPigletByPrimaryDataId = async (primaryDataId: string) =>
  axios.delete(`${endpoint}/piglet-footprints/${primaryDataId}/primary-data`);

export const getPigletHerdDetails = async (primaryDataId: string): Promise<FootprintingPigletHerdDetails> =>
  axios.get(`${endpoint}/piglet-footprints/${primaryDataId}/herd-details`);

export const setPigletHerdDetails = async (
  data: Partial<Omit<FootprintingPigletHerdDetails, "id" | "primaryDataId">>,
  primaryDataId?: string
): Promise<FootprintingPigletHerdDetails> => {
  if (primaryDataId) {
    return axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/herd-details`, data);
  }

  return axios.post(`${endpoint}/piglet-footprints/herd-details`, data);
};

export const getPigletFeedInformation = async (primaryDataId: string): Promise<UploadablePigletFeedInformation[]> =>
  axios.get(`${endpoint}/piglet-footprints/${primaryDataId}/feed-informations`);

export const setPigletFeedInformation = async (primaryDataId: string, data: UploadablePigletFeedInformation[]): Promise<FootprintingPigletFeedInformation> =>
  axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/feed-informations`, data);

export const getFootprintingPigletFeedProduction = async (feedInformationId: string): Promise<PigletFeedProduction | null> =>
  axios.get(`${endpoint}/piglet-footprints/piglet-feed-productions/${feedInformationId}`);

export const setFootprintingPigletFeedProduction = async (feedInformationId: string, data: PigletFeedProduction): Promise<PigletFeedProduction> =>
  axios.put(`${endpoint}/piglet-footprints/piglet-feed-productions/${feedInformationId}`, data);

export const getFootprintingPigletManureStorage = async (primaryDataId: string): Promise<UploadableManureStorage[]> =>
  axios.get(`${endpoint}/piglet-footprints/${primaryDataId}/manure-storages`);

export const setFootprintingPigletManureStorage = async (
  data: FootprintingPigletManureStoragePayload,
  primaryDataId?: string
): Promise<UploadableManureStorage[]> => axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/manure-storages`, data.selectedManureStorage);

export const getFootprintingPigletManureAdditive = async (primaryDataId: string): Promise<UploadableManureAdditive[]> =>
  axios.get(`${endpoint}/piglet-footprints/${primaryDataId}/manure-additives`);

export const setFootprintingPigletManureAdditive = async (
  data: FootprintingPigletManureAdditivePayload,
  primaryDataId?: string
): Promise<UploadableManureAdditive[]> => axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/manure-additives`, data.selectedManureAdditive);

export const setPigletStables = async (data: FootprintingPigletStablesPayload, primaryDataId: string): Promise<FootprintingPigletStablesPayload> =>
  axios.put(`${endpoint}/piglet-footprints/${primaryDataId}/stables`, data);
